import type { OdooTask } from '@/types/odoo';
import { formatDateKey, isFeestdagMarker, isVerlofTask, type EasykitGroupingConfig } from './planning';

/** "Per dag" board: date key → the cards of that day, in display order. */
export type DayColumns = Map<string, OdooTask[]>;

/** Start time of a task ("16:00:00"), or '' when it carries a date only. */
function startTime(t: OdooTask): string {
  return typeof t.date_deadline === 'string' ? t.date_deadline.slice(11) : '';
}

/**
 * Group the board's tasks into one column per day, with no installer rows.
 *
 * - A task appears ONCE per day, however many installers it has (deduplicated by id).
 * - Verlof is left out; feestdag markers are not cards (the board marks the column).
 * - EasyKit work collapses into one "Easykit tour" card per installer per day, like
 *   the flat Installateurs view, so the card's installer chip names who drives it.
 * - Within a day: by start time, then project name, then id (stable).
 */
export function groupTasksPerDay(
  tasks: OdooTask[],
  weekDays: Date[],
  projectNameMap: Map<number, string>,
  easykitConfig?: EasykitGroupingConfig | null,
): DayColumns {
  const columns: DayColumns = new Map(weekDays.map((d) => [formatDateKey(d), []]));
  const seen = new Set<string>();
  // (installerId, dateKey) → real EasyKit tasks behind one tour card
  const easykitDayTasks = new Map<string, OdooTask[]>();

  for (const task of tasks) {
    if (typeof task.date_deadline !== 'string') continue;
    const dateKey = task.date_deadline.slice(0, 10);
    const column = columns.get(dateKey);
    if (!column || isFeestdagMarker(task) || isVerlofTask(task)) continue;

    if (task.is_easykit && easykitConfig) {
      const installerIds = task.x_studio_installateur?.length ? task.x_studio_installateur : [0];
      for (const instId of installerIds) {
        const pairKey = `${instId}:${dateKey}`;
        const existing = easykitDayTasks.get(pairKey);
        if (existing) existing.push(task);
        else easykitDayTasks.set(pairKey, [task]);
      }
      continue;
    }

    const seenKey = `${task.id}:${dateKey}`;
    if (seen.has(seenKey)) continue;
    seen.add(seenKey);
    column.push(task);
  }

  // Sort key per card; a tour card sorts by its earliest real task.
  const sortTime = new Map<OdooTask, string>();
  let syntheticId = -2000;
  for (const [pairKey, realTasks] of easykitDayTasks) {
    const [instIdStr, dateKey] = pairKey.split(':');
    const instId = Number(instIdStr);
    const summaryTask: OdooTask = {
      id: syntheticId--,
      name: `${realTasks.length} ${realTasks.length === 1 ? 'taak' : 'taken'}`,
      description: false,
      project_id: [0, 'Easykit tour'],
      user_ids: [],
      stage_id: false,
      date_deadline: dateKey,
      priority: '0',
      tag_ids: [],
      x_studio_installateur: instId === 0 ? [] : [instId],
      is_easykit: true,
      _easykitDayTasks: realTasks,
    };
    sortTime.set(summaryTask, realTasks.map(startTime).sort()[0] ?? '');
    columns.get(dateKey)!.push(summaryTask);
  }

  const projectName = (t: OdooTask): string => {
    const soId = Array.isArray(t.sale_order_id) ? t.sale_order_id[0] : 0;
    return projectNameMap.get(soId) || (t.project_id ? String(t.project_id[1]) : '');
  };

  for (const column of columns.values()) {
    column.sort((a, b) => {
      const ta = sortTime.get(a) ?? startTime(a);
      const tb = sortTime.get(b) ?? startTime(b);
      if (ta !== tb) return ta < tb ? -1 : 1;
      const byName = projectName(a).localeCompare(projectName(b), 'nl');
      if (byName !== 0) return byName;
      // Tour cards at the same time: by installer, not by their synthetic id.
      const byInstaller = (a.x_studio_installateur?.[0] ?? 0) - (b.x_studio_installateur?.[0] ?? 0);
      if (a.id < 0 && b.id < 0 && byInstaller !== 0) return byInstaller;
      return a.id - b.id;
    });
  }

  return columns;
}

/** Unique cards on the "Per dag" board (the header's "x van y taken"). */
export function countPerDayTasks(columns: DayColumns): number {
  const ids = new Set<number>();
  for (const column of columns.values()) for (const t of column) ids.add(t.id);
  return ids.size;
}

/** One horizontal band of the "Per dag" board: a projectleider's cards across the days. */
export interface DayBand {
  /** 'pl:<id>', or 'alle' / 'zonder' for the two bottom bands. */
  key: string;
  label: string;
  columns: DayColumns;
}

/**
 * Split the day columns into one band per projectleider (x_studio_projectleider,
 * as in the PL + Inst. view), ordered by name; then "Alle projectleiders" for tasks
 * marked for every PL; then "Zonder projectleider" at the bottom, which also holds
 * the EasyKit tour cards (they carry no PL). Each card lands in exactly one band,
 * and the day order is kept. Only bands with at least one card are returned.
 */
export function splitPerDayIntoBands(columns: DayColumns): DayBand[] {
  const emptyDays = (): DayColumns => new Map([...columns.keys()].map((dk) => [dk, []]));
  const plBands = new Map<number, DayBand>();
  const alle: DayBand = { key: 'alle', label: 'Alle projectleiders', columns: emptyDays() };
  const zonder: DayBand = { key: 'zonder', label: 'Zonder projectleider', columns: emptyDays() };
  const bandFor = (t: OdooTask): DayBand => {
    if (t.x_studio_alle_projectleiders) return alle;
    if (!t.x_studio_projectleider) return zonder;
    const [id, name] = t.x_studio_projectleider;
    let band = plBands.get(id);
    if (!band) {
      band = { key: `pl:${id}`, label: name, columns: emptyDays() };
      plBands.set(id, band);
    }
    return band;
  };

  for (const [dateKey, column] of columns) {
    for (const task of column) bandFor(task).columns.get(dateKey)!.push(task);
  }

  const named = [...plBands.values()].sort(
    (a, b) => a.label.localeCompare(b.label, 'nl') || a.key.localeCompare(b.key),
  );
  return [...named, alle, zonder].filter((band) =>
    [...band.columns.values()].some((c) => c.length > 0),
  );
}
